import {Navigate, Outlet, useLocation} from 'react-router-dom'
import {useOktaAuth} from '@okta/okta-react'
import {oktaAccessToken} from '@/config/oktaConfig'
import LoadingSpinner from '@/components/GDS/atoms/LoadingSpinner/LoadingSpinner'
import {useAccess} from '@/hooks/useAccess'
import {useMemo} from 'react'
import {getDefaultRoute} from '@/layout/helpers/layoutHelpers'
import {
  ASK_DEVLENS_AVAILABILITY_FLAG,
  ASK_DEVLENS_PAGE_SLUG,
  getPageSlugForPath,
  isAccessRequiredForPath,
} from '@/routes/routeConfig'
import NoAccess from '@/components/common/NoAccess/NoAccess'
import {useFeatureFlag} from '@/contexts/GrowthBookContext'
import {AuthService} from '@/services/authService'

const ProtectedRoute = () => {
  const {authState} = useOktaAuth()
  const {hasAccess, userRoles, acl, aclLoaded} = useAccess()
  const location = useLocation()
  const askDevLensAvailable = useFeatureFlag(ASK_DEVLENS_AVAILABILITY_FLAG) === true

  // Resolve the page slug for the current route
  const pageSlug = useMemo(() => getPageSlugForPath(location.pathname), [location.pathname])
  const accessRequired = useMemo(() => isAccessRequiredForPath(location.pathname), [location.pathname])

  // Check if user has access to the current route via ACL
  const hasRequiredAccess = useMemo(() => {
    // Don't check access until ACL is loaded
    if (!accessRequired) return true

    const featureIsAvailable = pageSlug !== ASK_DEVLENS_PAGE_SLUG || askDevLensAvailable
    return featureIsAvailable && hasAccess(pageSlug)
  }, [accessRequired, hasAccess, pageSlug, aclLoaded, askDevLensAvailable])

  // The backend token contains the roles used for authorization.
  const accessToken = oktaAccessToken()
  const isAuthorizationInitializing =
    authState?.isAuthenticated === undefined ||
    (authState.isAuthenticated === true && Boolean(accessToken) && (!AuthService.hasBackendToken() || !aclLoaded))

  if (isAuthorizationInitializing) {
    return <LoadingSpinner />
  }

  // Redirect to login if not authenticated or no valid token
  if (!authState?.isAuthenticated || !accessToken) {
    const returnUrl = `${location.pathname}${location.search}`
    sessionStorage.setItem('returnUrl', returnUrl)
    return <Navigate to="/login" replace />
  }

  // No roles assigned → show "contact admin" message
  if (userRoles.length === 0) {
    return <NoAccess />
  }

  // Redirect to the user's default route if they don't have access to this page
  if (!hasRequiredAccess) {
    const defaultRoute = getDefaultRoute(userRoles, acl)
    return <Navigate to={defaultRoute} replace />
  }

  // Render the protected content if authenticated and has required access
  return <Outlet />
}

export default ProtectedRoute
